import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, Typography, Box, TextField, Button, IconButton, Collapse, Select, MenuItem, InputLabel, FormControl, Checkbox, ListItemText, OutlinedInput, SelectChangeEvent, FormHelperText, Dialog, DialogTitle, DialogContent, DialogActions, Divider, useMediaQuery, useTheme } from '@mui/material';
import { AddCircle, RemoveCircle, ModeEdit, Delete, Close } from '@mui/icons-material';
import PeopleIcon from '@mui/icons-material/People';
import { Employee, Service } from '../../../types';
import { formatMinutes } from '../../../time/timeUtils';
import CustomDialog from '../../homepage/CustomDialog';
import ImageSelect from '../../global/images/ImageSelector';
import { createBookingWebsiteDbRef } from '../../../firebase/firebase-references';
import ImageViewer from '../../global/images/imageViewer';
import { TIME_INTERVAL } from '../../../constants';
import { WEBSITE_ID } from '../../../projectConfig';
import { useQuery } from '@tanstack/react-query';
import { getCurrentBusinessConfig } from '../../../firebase/firebase-delegate';
import { resolveBookingUxConfig } from '../../../utils/bookingUxConfig';

interface EditableServiceCardProps {
  initialService: Service;
  employees: Employee[];
  onSaveService: (oldService: Service, updatedService: Service, serviceImage?: File) => void;
  onDeleteService: (service: Service) => void;
  siteId?: string;
  resourcePluralLabel?: string;
}

const MAX_SERVICE_TITLE_LENGTH = 30;
const MAX_SERVICE_DESCRIPTION_LENGTH = 200;

const EditableServiceCard: React.FC<EditableServiceCardProps> = ({
  initialService,
  employees,
  onSaveService,
  onDeleteService,
  siteId = WEBSITE_ID,
  resourcePluralLabel = 'Employees',
}) => {
  const [currentService, setCurrentService] = useState<Service>({ ...initialService }); //this ensures its a copy
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [errors, setErrors] = useState<{ title?: string; price?: string; time?: string; employees?: string }>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


  const businessConfigQuery = useQuery({
    queryKey: ['businessConfig'],
    queryFn: () => getCurrentBusinessConfig(),
  });
  const isResourceMode = resolveBookingUxConfig(businessConfigQuery.data).bookingModel === 'resource';


  useEffect(() => {
    setCurrentService({ ...initialService });
  }, [initialService]);

  const employeeNameMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    employees.forEach((employee) => {
      map[employee.id] = isResourceMode ? employee.firstName : `${employee.firstName} ${employee.lastName}`;
    });
    return map;
  }, [employees, isResourceMode]);

  const handleFieldChange = (
    field: keyof Service,
    value: string | number | boolean | string[]
  ) => {
    setCurrentService((prev) => ({ ...prev, [field]: value }));
  };

  const handleDurationChange = (increment: boolean) => {
    setCurrentService((prev) => ({
      ...prev,
      time: increment ? prev.time + TIME_INTERVAL : Math.max(prev.time - TIME_INTERVAL, 0),
    }));
  };

  const handleEmployeeChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setCurrentService((prev) => ({
      ...prev,
      employees: typeof value === 'string' ? value.split(',') : value,
    }));
    if (errors.employees) {
      setErrors((prev) => ({ ...prev, employees: undefined }));
    }
  };

  const toggleEditing = () => {
    if (isEditing) {
      // If editing is being canceled, reset currentService to the initial service
      setCurrentService(initialService);
    }
    setIsEditing(!isEditing);
  };

  const isDirty = useMemo(() => {
    const normalize = (value?: string) => (value ?? '').trim();
    const sameArray = (a: string[] = [], b: string[] = []) => {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, idx) => val === sortedB[idx]);
    };
    if (imageFile) return true;
    if (normalize(currentService.title) !== normalize(initialService.title)) return true;
    if (normalize(currentService.description) !== normalize(initialService.description)) return true;
    if (Number(currentService.price) !== Number(initialService.price)) return true;
    if (Number(currentService.time) !== Number(initialService.time)) return true;
    if ((currentService.category ?? '') !== (initialService.category ?? '')) return true;
    if (!sameArray(currentService.employees, initialService.employees)) return true;
    if ((currentService.imagePath ?? '') !== (initialService.imagePath ?? '')) return true;
    return false;
  }, [currentService, initialService, imageFile]);

  const handleSave = () => {
    if (!isDirty) return;
    const nextErrors: typeof errors = {};
    const trimmedTitle = currentService.title?.trim() ?? '';
    if (!trimmedTitle) {
      nextErrors.title = 'Title is required.';
    } else if (trimmedTitle.length > MAX_SERVICE_TITLE_LENGTH) {
      nextErrors.title = `Title must be ${MAX_SERVICE_TITLE_LENGTH} characters or fewer.`;
    }

    const priceValue = Number(currentService.price);
    if (!priceValue || priceValue <= 0) {
      nextErrors.price = 'Price must be greater than 0.';
    }

    const timeValue = Number(currentService.time);
    if (!timeValue || timeValue <= 0) {
      nextErrors.time = 'Duration is required.';
    }

    if (!currentService.employees.length) {
      nextErrors.employees = 'Select at least one employee.';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSaveService(
      initialService,
      {
        ...currentService,
        title: trimmedTitle,
        description: currentService.description?.trim() ?? '',
      },
      imageFile
    );
    toggleEditing();
  };

  const handleDeleteConfirm = () => {
    onDeleteService(currentService);
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleImageChange = (imageFile?: File) => {
    setCurrentService((prev) => {
      const updatedService = { ...prev };

      if (!imageFile) {
        // Remove the imagePath key if no image is selected
        delete updatedService.imagePath;
      } else {
        let db_ref = createBookingWebsiteDbRef(siteId, `services/${currentService.title.replace(/\s+/g, '_')}`);
        updatedService.imagePath = db_ref;
      }
      return updatedService;
    });

    setImageFile(imageFile);

    // Enable editing mode if an image is added/changed
    if (!isEditing) {
      setIsEditing(true);
    }
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const editFields = (
    <>
      <ImageSelect
        initialImagePath={initialService.imagePath}
        onImageChange={handleImageChange}
        enabled={isEditing}
        altText="Service Image"
      />
      <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Title"
              type="text"
              value={currentService.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              fullWidth
              inputProps={{ maxLength: MAX_SERVICE_TITLE_LENGTH }}
              helperText={
                errors.title
                  ? errors.title
                  : `${(currentService.title ?? '').length}/${MAX_SERVICE_TITLE_LENGTH}`
              }
              error={Boolean(errors.title)}
            />

            <TextField
              label="Description"
              value={currentService.description ?? ''}
              onChange={(e) => handleFieldChange('description', e.target.value.slice(0, MAX_SERVICE_DESCRIPTION_LENGTH))}
              fullWidth
              multiline
              minRows={2}
              placeholder={`Describe the service (max ${MAX_SERVICE_DESCRIPTION_LENGTH} characters)`}
              inputProps={{ maxLength: MAX_SERVICE_DESCRIPTION_LENGTH }}
              helperText={`${(currentService.description ?? '').length}/${MAX_SERVICE_DESCRIPTION_LENGTH}`}
            />

            <Box display="flex" alignItems="center">
              <IconButton onClick={() => handleDurationChange(false)}>
                <RemoveCircle color='primary' />
              </IconButton>
              <TextField
                label="Duration"
                value={formatMinutes(currentService.time)}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
                fullWidth
                sx={{ textAlign: 'center' }}
                error={Boolean(errors.time)}
                helperText={errors.time}
              />
              <IconButton onClick={() => handleDurationChange(true)}>
                <AddCircle />
              </IconButton>
            </Box>

            <TextField
              label="Price"
              type="number"
              value={currentService.price}
              onChange={(e) => handleFieldChange('price', parseFloat(e.target.value))}
              fullWidth
              error={Boolean(errors.price)}
              helperText={errors.price}
            />
            <FormControl fullWidth>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                value={currentService.category ?? ''}
                onChange={(e) =>
                  setCurrentService((prev) => ({
                    ...prev,
                    category: e.target.value === '' ? undefined : e.target.value,
                  }))
                }
                input={<OutlinedInput label="Category" />}
              >
                <MenuItem value="">
                  <em>Uncategorized</em>
                </MenuItem>
                {businessConfigQuery.data?.preferences?.serviceCategories?.map((category, index) => (
                  <MenuItem key={index} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>



            {/* Employee selection */}
            <FormControl fullWidth>
              <InputLabel id="employees-label">{resourcePluralLabel}</InputLabel>
              <Select
                labelId="employees-label"
                multiple
                value={currentService.employees}
                onChange={handleEmployeeChange}
                input={<OutlinedInput label={resourcePluralLabel} />}
                renderValue={(selected) => selected.map(id => employeeNameMap[id]).join(', ')}
                MenuProps={MenuProps}
                error={Boolean(errors.employees)}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    <Checkbox checked={currentService.employees.indexOf(employee.id) > -1} />
                    <ListItemText primary={employeeNameMap[employee.id]} />
                  </MenuItem>
                ))}
              </Select>
              {errors.employees && (
                <FormHelperText error>{errors.employees}</FormHelperText>
              )}
            </FormControl>
      </Box>
    </>
  );

  const editActions = (
    <DialogActions
      sx={{
        position: 'sticky',
        bottom: 0,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 2,
        py: 1,
        gap: 0,
        justifyContent: 'space-between',
        '& > :not(:first-of-type)': { ml: 0 },
      }}
    >
      <Button onClick={toggleEditing} sx={{ flex: 1, borderRadius: '4px', py: 1.2, ml: 0 }}>
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        color="primary"
        sx={{ flex: 1, borderRadius: '4px', py: 1.2, ml: 0 }}
        disabled={!isDirty}
      >
        Save
      </Button>
    </DialogActions>
  );

  return (
    <Card sx={{ mb: 2, borderRadius: '12px', boxShadow: 3 }}>
      <CardContent
        sx={{
          p: 1,
          pb: isEditing ? 0 : 1,
          '&:last-child': { pb: isEditing ? 0 : 1 },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={1} sx={{ paddingLeft: 2, paddingRight: 0, flexWrap: 'nowrap', mb: 1.5 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h6" component="div" noWrap>
              {isEditing ? 'Edit Service' : currentService.title}
            </Typography>
          </Box>
          <Box sx={{ flexShrink: 0, display: 'flex' }}>
            {isEditing ? (
              <IconButton onClick={toggleEditing} color="primary">
                <Close />
              </IconButton>
            ) : (
              <IconButton onClick={toggleEditing} color="primary">
                <ModeEdit />
              </IconButton>
            )}
            {(!isEditing || !isMobile) && (
              <IconButton onClick={handleDeleteClick} color="error">
                <Delete />
              </IconButton>
            )}
          </Box>
        </Box>

        {!isEditing && currentService.imagePath && (
          <ImageViewer
            imagePath={currentService.imagePath}
            altText="Service Image"
          />
        )}

        <Collapse in={!isEditing} sx={{ paddingX: 2 }}>
          <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
            {formatMinutes(currentService.time)} | ${currentService.price.toFixed(2)}
          </Typography>
          <Typography variant="body1"><PeopleIcon sx={{ verticalAlign: 'middle', mr: 0.5 }} />{currentService.employees.map(id => employeeNameMap[id]).join(', ')}</Typography>
          {currentService.category && (
            <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic', color: 'text.secondary' }}>
              Category: {currentService.category}
            </Typography>
          )}
        </Collapse>

        {!isMobile && (
          <Collapse in={isEditing} sx={{ p: 1, pb: 0 }}>
            {editFields}
            <Box sx={{ mt: 2, mx: -1 }}>
          <Box sx={{ mx: -2 }}>
            {editActions}
          </Box>
            </Box>
          </Collapse>
        )}
      </CardContent>

      {isMobile && isEditing && (
        <Dialog open={isEditing} onClose={toggleEditing} fullScreen={isMobile}>
          <DialogTitle sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-start' }} />
              <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                Edit Service
              </Typography>
              <IconButton onClick={toggleEditing} aria-label="Close">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 0 }}>
            {editFields}
          </DialogContent>
          <Box sx={{ mt: 2 }}>
            {editActions}
          </Box>
        </Dialog>
      )}

      <CustomDialog
        title="Confirm Deletion"
        description="Are you sure you want to delete this service? This action cannot be undone."
        actionButtonText="Delete"
        onAction={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        open={isDeleteDialogOpen}
      />
    </Card>
  );
};

export default EditableServiceCard;
